import { getEntityMasterFilterOptions } from '@api/entity-attributes';
import { listVendorsForPicker } from '@api/work-order';

/** Currencies to offer when the merchant has recorded none of their own yet. */
export const SUGGESTED_CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'CNY', 'AED'];

/**
 * Left-aligned under its input. The theme caps every Select dropdown at 268px and Mantine
 * centres it, which in a full-width field reads as a dropdown shoved to the right. Mantine
 * replaces the theme's default object when a component passes its own, so `position` must be
 * restated here.
 */
export const FULL_WIDTH_DROPDOWN = { width: 'target', position: 'bottom-start' } as const;

export type SelectOption = { value: string; label: string };

/** One maker as a form holds it. `price` blank means "no price agreed yet". */
export interface MakerDraft {
    vendorId: string | null;
    price: number | '';
    currency: string | null;
}

export const mergeCurrencies = (own: string[]): string[] => {
    const seen = new Set<string>();
    const options: string[] = [];

    for (const value of [...own, ...SUGGESTED_CURRENCIES]) {
        const key = value.trim().toUpperCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        options.push(key);
    }

    return options;
};

/** Manufacturers plus untagged vendors, labelled "Name — ID", the list every maker picker uses. */
export const loadMakerVendorOptions = async (): Promise<SelectOption[]> => {
    const vendors = await listVendorsForPicker().catch(() => []);
    return vendors.map((v) => ({
        value: v.vendorId,
        label: v.name && v.name !== v.vendorId ? `${v.name} — ${v.vendorId}` : v.vendorId,
    }));
};

/** The brand's own currencies ahead of the suggested ones. */
export const loadCurrencyOptions = async (): Promise<string[]> => {
    try {
        const options = await getEntityMasterFilterOptions('VENDOR_PRODUCT_MAPPING');
        return mergeCurrencies(options.currency ?? []);
    } catch {
        return SUGGESTED_CURRENCIES;
    }
};
